import { Fragment, useEffect, useMemo, useState } from 'react';
import { Page } from '../theme/elements/Page';
import { Box, Divider, Stack, Typography, useTheme } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { getUserDetails } from 'state/reducers/user.reducers';
import { getTimeOfDay, greetingEnum } from 'constants/global.constants';
import { getAllAnalyticsPendingRequests } from 'services/pending-requests.service';
import { permissionEnums } from 'constants/permission.constants';
import { hasPermission } from 'components/AuthorizationComponent';
import { HouseAccountBalances } from 'sections/Dashboard/HouseAccountBalances';
import { OpenedRequests } from 'sections/Dashboard/OpenedRequests';
import { HyperMonitoring } from 'sections/Dashboard/HyperMonitoring';
import { DigitalBalanceRequests } from 'sections/Dashboard/DigitalBalanceRequests';
import { SearchComponent } from 'components/SearchComponent/SearchComponent';
import { getError as getBalanceErrorMessage, setError as setBalanceError } from 'state/reducers/balance.reducer';
import {
	getError as getPendingRequestsErrorMessage,
	setError as setPendingRequestError,
} from 'state/reducers/pending-requests.reducer';
import { WarningDialog } from 'components/WarningDialog';

export const DashboardApp = () => {
	const dispatch = useAppDispatch();
	const t = useAppSelector(selectTranslations);
	const theme = useTheme();
	const [pageGreeting, setPageGreeting] = useState('');
	const { firstName } = useAppSelector(getUserDetails);
	const pagePermission = hasPermission([permissionEnums.CLIENTS_ACTIVITY_DASHBOARD]);
	const pendingRequestsPermission = hasPermission([permissionEnums.DASHBOARD_PENDING_REQUESTS_ANALYTICS]);
	const houseAccountBalancePermission = hasPermission([permissionEnums.HOUSE_ACCOUNT_BALANCE]);
	const searchPermission = hasPermission([permissionEnums.FREE_TEXT_SEARCH_DATABASE]);
	const hyperMonitoringPermission = hasPermission([permissionEnums.VIEW_HYPER_MONITORING]);
	const pagePrefix = 'page.dashboard.search.';
	const balanceErrorMessage = useAppSelector(getBalanceErrorMessage);
	const pendingRequestsErrorMessage = useAppSelector(getPendingRequestsErrorMessage);

	useEffect(() => {
		if (pagePermission) {
			setTimeOfDay();
			dispatch(getAllAnalyticsPendingRequests());
		}
	}, [pagePermission]);

	const setTimeOfDay = () => {
		switch (getTimeOfDay()) {
			case greetingEnum.MORNING:
				setPageGreeting(`${t('greeting.morning')}`);
				break;
			case greetingEnum.NOON:
				setPageGreeting(`${t('greeting.noon')}`);
				break;
			case greetingEnum.AFTERNOON:
				setPageGreeting(`${t('greeting.afternoon')}`);
				break;
			case greetingEnum.EVENING:
				setPageGreeting(`${t('greeting.evening')}`);
				break;
			default:
				setPageGreeting(`${t('greeting.hello')}`);
				break;
		}
	};

	const openDialog = useMemo(() => {
		return balanceErrorMessage.exist || pendingRequestsErrorMessage.exist;
	}, [balanceErrorMessage, pendingRequestsErrorMessage]);

	const currentSetErrorMethod: any = useMemo(() => {
		if (balanceErrorMessage.exist) return setBalanceError;
		else if (pendingRequestsErrorMessage.exist) return setPendingRequestError;
		else return null;
	}, [balanceErrorMessage, pendingRequestsErrorMessage]);

	const currentErrorMessage: any = useMemo(() => {
		return openDialog ? balanceErrorMessage || pendingRequestsErrorMessage : null;
	}, [balanceErrorMessage, pendingRequestsErrorMessage]);

	return (
		<Fragment>
			<Page title="">
				<Stack spacing={7}>
					<WarningDialog
						open={openDialog}
						subTitle={currentErrorMessage?.text}
						handleCancel={() => {
							currentSetErrorMethod && dispatch(currentSetErrorMethod({ exist: false }));
						}}
					/>
					<Stack spacing={4}>
						<Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
							<Typography variant="pageTitle">
								{pageGreeting}, {firstName}
							</Typography>
						</Box>
						{searchPermission && (
							<Stack spacing={3}>
								<Box>
									<Typography
										variant="h4"
										sx={{ fontWeight: 700, color: theme.palette.common.black }}
									>
										{t(`${pagePrefix}title`)}
									</Typography>
									<Typography variant="subtitle" sx={{ color: theme.palette.root.grey }}>
										{t(`${pagePrefix}subtitle`)}
									</Typography>
								</Box>
								<Stack spacing={4}>
									<SearchComponent />
									<Divider
										orientation="horizontal"
										flexItem
										sx={{ borderBottom: 1, borderColor: theme.palette.root.lightGreen }}
									/>
								</Stack>
							</Stack>
						)}
					</Stack>
					{pendingRequestsPermission && (
						<Stack spacing={4}>
							<OpenedRequests />
							<Divider
								orientation="horizontal"
								flexItem
								sx={{ borderBottom: 1, borderColor: theme.palette.root.lightGreen }}
							/>
						</Stack>
					)}
					{hyperMonitoringPermission && (
						<Stack spacing={4}>
							<HyperMonitoring />
							<Divider
								orientation="horizontal"
								flexItem
								sx={{ borderBottom: 1, borderColor: theme.palette.root.lightGreen }}
							/>
						</Stack>
					)}
					{pendingRequestsPermission && (
						<Stack spacing={4}>
							<DigitalBalanceRequests />
							<Divider
								orientation="horizontal"
								flexItem
								sx={{ borderBottom: 1, borderColor: theme.palette.root.lightGreen }}
							/>
						</Stack>
					)}
					{houseAccountBalancePermission && (
						<Stack spacing={4}>
							<HouseAccountBalances />
							<Divider
								orientation="horizontal"
								flexItem
								sx={{ borderBottom: 1, borderColor: theme.palette.root.lightGreen }}
							/>
						</Stack>
					)}
				</Stack>
			</Page>
		</Fragment>
	);
};
